import { useEffect, useRef } from "react";
import Draggable from "react-draggable";
import "../../css/common/modals.css";
import { useTheme } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import IconButton from "@mui/material/IconButton";

interface ModalProps {
  children: React.ReactNode;
  isOpen: boolean;
  onClose: () => void;
}

interface BaseModalProps extends ModalProps {
  className?: string;
  closeButton?: boolean;
  title?: string;
}

/**
 * A dim modal window customizable
 * */
export const BaseModal: React.FC<BaseModalProps> = ({ children, isOpen, onClose, title, className, closeButton = true }) => {
  const theme = useTheme();
  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
        onClose(); // Close the modal if the click is outside
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div
      className="modal"
      style={
        {
          "--color-modal-background": theme.palette.background.default,
        } as React.CSSProperties
      }
    >
      <div
        className={"modal-content " + (className ?? "")}
        ref={modalRef}
        style={{
          position: "relative",
          width: "auto",
          height: "auto",
          padding: "3%",
          overflow: "visible",
        }}
      >
        <IconButton
          onClick={onClose}
          sx={{
            position: "absolute",
            top: "8px",
            right: "8px",
          }}
        >
          <CloseIcon />
        </IconButton>
        {/* Title */}
        {title && (
          <h2
            style={{
              margin: "0",
              padding: "0",
              textAlign: "center",
            }}
          >
            {title}
          </h2>
        )}

        {children}
      </div>
    </div>
  );
};

interface DraggableModalProps extends ModalProps {
  draggable?: boolean;
  className?: string;
}

/**
 * A draggable modal window customizable
 * */
export const DraggableModal: React.FC<DraggableModalProps> = ({ children, isOpen, onClose, className, draggable = true }) => {
  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => (modalRef.current && !modalRef.current.contains(event.target as Node) ? onClose() : null);

    if (isOpen) document.addEventListener("mousedown", handleClickOutside);

    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <Draggable disabled={!draggable}>
      <div className={"draggable-modal-content " + (className ?? "")} ref={modalRef}>
        {children}
      </div>
    </Draggable>
  );
};
